import React, { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Divider,
  Link,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ArrowForward,
  OpenInNew,
  ArrowBack,
  AccessTime,
  CalendarToday,
  Person,
  LinkedIn,
} from '@mui/icons-material';
import ArticleTable from '../../components/Blog/ArticleTable';
import SEOHead from '../../components/SEO/SEOHead';
import BreadcrumbNav from '../../components/SEO/BreadcrumbNav';
import AtomicAnswer from '../../components/SEO/AtomicAnswer';
import FAQSection from '../../components/SEO/FAQSection';
import NewsletterSection from '../../components/Layout/NewsletterSection';
import TrustStrip from '../../components/Layout/TrustStrip';
import RelatedArticles from '../../components/Blog/RelatedArticles';
import carlosPhoto from '../../assets/img/author-carlos.jpg';
import { useTranslation } from 'react-i18next';

const HERO_IMAGE = '/images/blog/primera-hipoteca-canada-llaves.jpg';
const AUTHOR = {
  name: 'Carlos Alviz',
  role: 'Co-fundador, FinMaple',
  linkedin: 'https://www.linkedin.com/in/alvizcarlos/',
  photo: carlosPhoto,
};

const PrimeraHipotecaPage = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();
  const [takeawaysOpen, setTakeawaysOpen] = useState(false);

  const TAGS = [t('blog.primeraHipoteca.tag1'), t('blog.primeraHipoteca.tag2')];

  const DOWN_PAYMENT_TABLE = [
    { precio: '$400,000', downPayment: '$20,000 (5%)', seguro: t('blog.primeraHipoteca.yes') },
    { precio: '$600,000', downPayment: '$35,000 (5% + 10%)', seguro: t('blog.primeraHipoteca.yes') },
    { precio: '$800,000', downPayment: '$55,000', seguro: t('blog.primeraHipoteca.yes') },
    { precio: '$1,500,000+', downPayment: '$300,000 (20%)', seguro: t('blog.primeraHipoteca.no') },
  ];

  const CMHC_PREMIUM_TABLE = [
    { downPayment: '5%', premium: '4.00%' },
    { downPayment: '10%', premium: '3.10%' },
    { downPayment: '15%', premium: '2.80%' },
  ];

  const FIJO_VS_VARIABLE = [
    {
      aspecto: t('blog.primeraHipoteca.fvAspect1'),
      fijo: t('blog.primeraHipoteca.fvFixed1'),
      variable: t('blog.primeraHipoteca.fvVar1'),
    },
    {
      aspecto: t('blog.primeraHipoteca.fvAspect2'),
      fijo: t('blog.primeraHipoteca.fvFixed2'),
      variable: t('blog.primeraHipoteca.fvVar2'),
    },
    {
      aspecto: t('blog.primeraHipoteca.fvAspect3'),
      fijo: t('blog.primeraHipoteca.fvFixed3'),
      variable: t('blog.primeraHipoteca.fvVar3'),
    },
    {
      aspecto: t('blog.primeraHipoteca.fvAspect4'),
      fijo: t('blog.primeraHipoteca.fvFixed4'),
      variable: t('blog.primeraHipoteca.fvVar4'),
    },
    {
      aspecto: t('blog.primeraHipoteca.fvAspect5'),
      fijo: t('blog.primeraHipoteca.fvFixed5'),
      variable: t('blog.primeraHipoteca.fvVar5'),
    },
  ];

  const FAQS = [
    {
      question: t('blog.primeraHipoteca.faq1Q'),
      answer: t('blog.primeraHipoteca.faq1A'),
    },
    {
      question: t('blog.primeraHipoteca.faq2Q'),
      answer: t('blog.primeraHipoteca.faq2A'),
    },
    {
      question: t('blog.primeraHipoteca.faq3Q'),
      answer: t('blog.primeraHipoteca.faq3A'),
    },
    {
      question: t('blog.primeraHipoteca.faq4Q'),
      answer: t('blog.primeraHipoteca.faq4A'),
    },
    {
      question: t('blog.primeraHipoteca.faq5Q'),
      answer: t('blog.primeraHipoteca.faq5A'),
    },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('blog.primeraHipoteca.seoTitle'),
    description: t('blog.primeraHipoteca.seoDescription'),
    datePublished: '2026-02-24',
    dateModified: '2026-02-24',
    author: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
    publisher: {
      '@type': 'Organization',
      name: 'FinMaple',
      url: 'https://www.finmaple.com',
      logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' },
    },
    mainEntityOfPage: 'https://www.finmaple.com/blog/primera-hipoteca-canada-guia',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.article-headline', '.atomic-answer'],
    },
    inLanguage: i18n.language,
  };

  return (
    <Box component="article" sx={{ bgcolor: '#F7F9FC' }}>
      <SEOHead
        title={t('blog.primeraHipoteca.seoTitle')}
        description={t('blog.primeraHipoteca.seoDescription')}
        canonical="/blog/primera-hipoteca-canada-guia"
        routeKey="blogHipoteca"
        seoKey="blogHipoteca"
        noIndex={i18n.language !== 'es'}
        ogImage={`https://www.finmaple.com${HERO_IMAGE}`}
        schema={articleSchema}
      />

      {/* Hero Image */}
      <Box
        role="img"
        aria-label={t('blog.primeraHipoteca.heroAlt')}
        sx={{
          width: '100%',
          height: { xs: 220, md: 360 },
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.45)), url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <Container maxWidth="md" sx={{ pb: 4 }}>
          <Button
            component={RouterLink}
            to={`/${lng}/blog`}
            startIcon={<ArrowBack />}
            sx={{ color: '#fff', textTransform: 'none', mb: 2, fontWeight: 500 }}
          >
            {t('blog.primeraHipoteca.backToGuides')}
          </Button>
        </Container>
      </Box>

      <TrustStrip />

      {/* Article Content */}
      <Container maxWidth="md" sx={{ mt: 0, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            px: { xs: 3, md: 5 },
            py: { xs: 3, md: 4 },
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          }}
        >
          <BreadcrumbNav items={[{ label: t('blog.primeraHipoteca.breadcrumbGuides'), to: `/${lng}/blog` }, { label: t('blog.primeraHipoteca.breadcrumbCurrent') }]} />
          {/* Tags */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {TAGS.map((tag, i) => (
              <Chip
                key={i}
                label={tag}
                size="small"
                sx={{
                  bgcolor: '#F0F2F5',
                  color: '#5F6B7A',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                }}
              />
            ))}
          </Box>

          {/* Title */}
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.2, mb: 3, fontSize: { xs: '1.75rem', md: '2.5rem' } }}
          >
            {t('blog.primeraHipoteca.title')}
          </Typography>

          {/* Meta */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              color: '#5F6B7A',
              mb: 3,
              fontSize: '0.9rem',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: 18 }} />
              <a
                href={AUTHOR.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', color: '#1E3A5F', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E3A5F' }}>
                  {AUTHOR.name}
                </Typography>
                <LinkedIn sx={{ fontSize: 16, color: '#1E3A5F' }} />
              </a>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 16 }} />
              <Typography variant="body2">{t('blog.primeraHipoteca.date')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 16 }} />
              <Typography variant="body2">{t('blog.primeraHipoteca.readTime')}</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* CTA Banner — comparador */}
          <Box
            sx={{
              bgcolor: '#F0FAF1', border: '1px solid #B8E0BA', borderRadius: '10px',
              p: 2.5, mb: 4, display: 'flex', alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between', flexDirection: { xs: 'column', md: 'row' }, gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.6 }}>
              <strong>{t('blog.primeraHipoteca.ctaBannerStrong')}</strong> {t('blog.primeraHipoteca.ctaBannerText')}
            </Typography>
            <Button
              component={RouterLink}
              to={`/${lng}/hipotecas`}
              variant="contained"
              size="small"
              sx={{
                bgcolor: '#1E3A5F', textTransform: 'none', fontWeight: 600, borderRadius: '8px',
                px: 2.5, flexShrink: 0, '&:hover': { bgcolor: '#1A3152' },
              }}
            >
              {t('blog.primeraHipoteca.ctaBannerButton')}
            </Button>
          </Box>

          {/* Atomic Answer */}
          <AtomicAnswer text={t('blog.primeraHipoteca.atomicAnswer')} />

          {/* Key Takeaways - Collapsible */}
          <Box
            sx={{
              border: '1px solid #EEF0F3',
              borderRadius: '10px',
              overflow: 'hidden',
              mt: 5,
              mb: 5,
            }}
          >
            <Box
              onClick={() => setTakeawaysOpen(!takeawaysOpen)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                bgcolor: '#F7F9FC',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#EEEEEE' },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('blog.primeraHipoteca.takeawaysTitle')}
              </Typography>
              {takeawaysOpen ? <ExpandLess sx={{ color: '#5F6B7A' }} /> : <ExpandMore sx={{ color: '#5F6B7A' }} />}
            </Box>
            <Box component="ul" sx={{ display: takeawaysOpen ? 'block' : 'none', px: 3, py: 2, m: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.6 } }}>
                <li>{t('blog.primeraHipoteca.takeaway1')}</li>
                <li>{t('blog.primeraHipoteca.takeaway2')}</li>
                <li>{t('blog.primeraHipoteca.takeaway3')}</li>
                <li>{t('blog.primeraHipoteca.takeaway4')}</li>
                <li>{t('blog.primeraHipoteca.takeaway5')}</li>
                <li>{t('blog.primeraHipoteca.takeaway6')}</li>
              </Box>
          </Box>

          {/* ================================================================ */}
          {/* H2: Cuanto puedes pagar */}
          {/* ================================================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t('blog.primeraHipoteca.s1Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s1P1')}
          </Typography>

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 1 }}>
            {t('blog.primeraHipoteca.s1GdsTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s1GdsP1a')}<strong>{t('blog.primeraHipoteca.s1GdsP1Strong')}</strong>{t('blog.primeraHipoteca.s1GdsP1b')}
          </Typography>

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 1 }}>
            {t('blog.primeraHipoteca.s1TdsTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s1TdsP1a')}<strong>{t('blog.primeraHipoteca.s1TdsP1Strong')}</strong>{t('blog.primeraHipoteca.s1TdsP1b')}
          </Typography>

          {/* Practical example box */}
          <Box
            sx={{
              bgcolor: '#F0FAF1',
              border: '1px solid #B8E0BA',
              borderRadius: '10px',
              p: 3,
              mb: 4,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2E7D32', mb: 1 }}>
              {t('blog.primeraHipoteca.s1ExampleTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t('blog.primeraHipoteca.s1ExLine1')}</strong> $70,000 ($5,833/{t('blog.primeraHipoteca.month')})<br />
              <strong>{t('blog.primeraHipoteca.s1ExLine2')}</strong> $5,833 x 0.39 = <strong>$2,275/{t('blog.primeraHipoteca.month')}</strong> {t('blog.primeraHipoteca.s1ExLine2b')}<br />
              <strong>{t('blog.primeraHipoteca.s1ExLine3')}</strong> {t('blog.primeraHipoteca.s1ExLine3b')}<br />
              <strong>{t('blog.primeraHipoteca.s1ExLine4')}</strong> $2,275 - $450 = ~$1,825/{t('blog.primeraHipoteca.month')}
            </Typography>
          </Box>

          {/* ================================================================ */}
          {/* H2: Stress test */}
          {/* ================================================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t('blog.primeraHipoteca.s2Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s2P1a')}<strong>{t('blog.primeraHipoteca.s2P1Strong')}</strong>{t('blog.primeraHipoteca.s2P1b')}
          </Typography>

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 1 }}>
            {t('blog.primeraHipoteca.s2HowTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s2HowP1a')}<strong>{t('blog.primeraHipoteca.s2HowP1Strong')}</strong>{t('blog.primeraHipoteca.s2HowP1b')}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li>{t('blog.primeraHipoteca.s2Li1')}</li>
            <li>{t('blog.primeraHipoteca.s2Li2')}</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s2P2a')}
            <strong>6.5%</strong>
            {t('blog.primeraHipoteca.s2P2b')}
            <strong>20%</strong>
            {t('blog.primeraHipoteca.s2P2c')}
            <RouterLink to={`/${lng}/calculadora-hipoteca`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t('blog.primeraHipoteca.s2CalcLink')}</RouterLink>
            {t('blog.primeraHipoteca.s2P2d')}
          </Typography>

          {/* Warning box */}
          <Box
            sx={{
              bgcolor: '#FFFBF0',
              border: '1px solid #FFE8A0',
              borderRadius: '10px',
              p: 3,
              mb: 4,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7D5A00', mb: 0.5 }}>
              {t('blog.primeraHipoteca.important')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              {t('blog.primeraHipoteca.s2WarningA')}<strong>{t('blog.primeraHipoteca.s2WarningStrong')}</strong>{t('blog.primeraHipoteca.s2WarningB')}
            </Typography>
          </Box>

          {/* ================================================================ */}
          {/* H2: Down payment */}
          {/* ================================================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t('blog.primeraHipoteca.s3Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s3P1')}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li><strong>5%</strong> {t('blog.primeraHipoteca.s3Li1')}</li>
            <li><strong>10%</strong> {t('blog.primeraHipoteca.s3Li2')}</li>
            <li><strong>20%</strong> {t('blog.primeraHipoteca.s3Li3')}</li>
          </Box>

          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            <strong>{t('blog.primeraHipoteca.s3NewStrong')}</strong> {t('blog.primeraHipoteca.s3NewP1a')}<strong>$1.5 {t('blog.primeraHipoteca.million')}</strong>{t('blog.primeraHipoteca.s3NewP1b')}<strong>{t('blog.primeraHipoteca.s3New30yrs')}</strong>{t('blog.primeraHipoteca.s3NewP1c')}
          </Typography>

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 1 }}>
            {t('blog.primeraHipoteca.s3SourcesTitle')}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li><strong>{t('blog.primeraHipoteca.s3Src1Label')}</strong> {t('blog.primeraHipoteca.s3Src1')}</li>
            <li><strong>{t('blog.primeraHipoteca.s3Src2Label')}</strong> {t('blog.primeraHipoteca.s3Src2')}</li>
            <li><strong>{t('blog.primeraHipoteca.s3Src3Label')}</strong> {t('blog.primeraHipoteca.s3Src3a')}<RouterLink to={`/${lng}/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t('blog.primeraHipoteca.s3Src3Link')}</RouterLink></li>
            <li><strong>{t('blog.primeraHipoteca.s3Src4Label')}</strong> {t('blog.primeraHipoteca.s3Src4')}</li>
          </Box>

          {/* Down payment table */}
          <ArticleTable
            columns={[
              { key: 'precio', label: t('blog.primeraHipoteca.tblDpCol1') },
              { key: 'downPayment', label: t('blog.primeraHipoteca.tblDpCol2') },
              { key: 'seguro', label: t('blog.primeraHipoteca.tblDpCol3') },
            ]}
            rows={DOWN_PAYMENT_TABLE}
          />

          {/* ================================================================ */}
          {/* H2: CMHC Insurance */}
          {/* ================================================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t('blog.primeraHipoteca.s4Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s4P1a')}<strong>{t('blog.primeraHipoteca.s4P1Strong')}</strong>{t('blog.primeraHipoteca.s4P1b')}
          </Typography>

          {/* Important distinction box */}
          <Box
            sx={{
              bgcolor: '#FFFBF0',
              border: '1px solid #FFE8A0',
              borderRadius: '10px',
              p: 3,
              mb: 3,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7D5A00', mb: 0.5 }}>
              {t('blog.primeraHipoteca.s4DistinctionTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              {t('blog.primeraHipoteca.s4DistinctionA')}<strong>{t('blog.primeraHipoteca.s4DistinctionStrong')}</strong>{t('blog.primeraHipoteca.s4DistinctionB')}
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s4P2a')}<strong>{t('blog.primeraHipoteca.s4P2Strong')}</strong>{t('blog.primeraHipoteca.s4P2b')}
          </Typography>

          {/* CMHC premium table */}
          <ArticleTable
            columns={[
              { key: 'downPayment', label: t('blog.primeraHipoteca.tblCmhcCol1') },
              { key: 'premium', label: t('blog.primeraHipoteca.tblCmhcCol2') },
            ]}
            rows={CMHC_PREMIUM_TABLE}
          />

          <Box
            sx={{
              bgcolor: '#F0FAF1',
              border: '1px solid #B8E0BA',
              borderRadius: '10px',
              p: 3,
              mb: 4,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2E7D32', mb: 1 }}>
              {t('blog.primeraHipoteca.s4ExampleTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t('blog.primeraHipoteca.s4ExLine1')}</strong><br />
              {t('blog.primeraHipoteca.s4ExLine2')}: $475,000<br />
              {t('blog.primeraHipoteca.s4ExLine3')} (4.00%): $475,000 x 0.04 = <strong>$19,000</strong><br />
              {t('blog.primeraHipoteca.s4ExLine4')}: $475,000 + $19,000 = <strong>$494,000</strong>
            </Typography>
          </Box>

          {/* ================================================================ */}
          {/* H2: Plazo, amortizacion, fijo vs variable */}
          {/* ================================================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t('blog.primeraHipoteca.s5Title')}
          </Typography>

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 1 }}>
            {t('blog.primeraHipoteca.s5TermTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s5TermP1a')}<strong>{t('blog.primeraHipoteca.s5TermStrong')}</strong>{t('blog.primeraHipoteca.s5TermP1b')}
          </Typography>

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 1 }}>
            {t('blog.primeraHipoteca.s5AmortTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s5AmortP1a')}<strong>{t('blog.primeraHipoteca.s5AmortStrong1')}</strong>{t('blog.primeraHipoteca.s5AmortP1b')}<strong>{t('blog.primeraHipoteca.s5AmortStrong2')}</strong>{t('blog.primeraHipoteca.s5AmortP1c')}
          </Typography>

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 3, mb: 1 }}>
            {t('blog.primeraHipoteca.s5FvTitle')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            {t('blog.primeraHipoteca.s5FvP1')}
          </Typography>

          {/* Fijo vs Variable table */}
          <ArticleTable
            columns={[
              { key: 'aspecto', label: t('blog.primeraHipoteca.tblFvCol1') },
              { key: 'fijo', label: t('blog.primeraHipoteca.tblFvCol2') },
              { key: 'variable', label: t('blog.primeraHipoteca.tblFvCol3') },
            ]}
            rows={FIJO_VS_VARIABLE}
          />

          {/* ================================================================ */}
          {/* H2: Pre-approval */}
          {/* ================================================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t('blog.primeraHipoteca.s6Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s6P1a')}
            <strong>{t('blog.primeraHipoteca.s6P1Strong')}</strong>
            {t('blog.primeraHipoteca.s6P1b')}
            <RouterLink to={`/${lng}/blog/construir-credito-canada-desde-cero`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t('blog.primeraHipoteca.s6CreditLink')}</RouterLink>
            {t('blog.primeraHipoteca.s6P1c')}
          </Typography>

          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            <strong>{t('blog.primeraHipoteca.s6RememberStrong')}</strong> {t('blog.primeraHipoteca.s6RememberText')}
          </Typography>

          <Typography variant="body1" component="h3" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 2 }}>
            {t('blog.primeraHipoteca.s6DocsTitle')}
          </Typography>
          <Box component="ol" sx={{ pl: 3, mb: 4, '& li': { mb: 1.5, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li><strong>{t('blog.primeraHipoteca.s6Doc1Label')}</strong> {t('blog.primeraHipoteca.s6Doc1')}</li>
            <li><strong>{t('blog.primeraHipoteca.s6Doc2Label')}</strong> {t('blog.primeraHipoteca.s6Doc2')}</li>
            <li><strong>{t('blog.primeraHipoteca.s6Doc3Label')}</strong> {t('blog.primeraHipoteca.s6Doc3')}</li>
            <li><strong>{t('blog.primeraHipoteca.s6Doc4Label')}</strong> {t('blog.primeraHipoteca.s6Doc4')}</li>
            <li><strong>{t('blog.primeraHipoteca.s6Doc5Label')}</strong> {t('blog.primeraHipoteca.s6Doc5')}</li>
          </Box>

          {/* Tip box for newcomers */}
          <Box
            sx={{
              bgcolor: '#F7F9FC',
              border: '1px solid #90CAF9',
              borderRadius: '10px',
              p: 3,
              mb: 4,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1E3A5F', mb: 0.5 }}>
              {t('blog.primeraHipoteca.s6TipTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              {t('blog.primeraHipoteca.s6TipText')}
            </Typography>
          </Box>

          {/* ================================================================ */}
          {/* H2: Costos de cierre */}
          {/* ================================================================ */}
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t('blog.primeraHipoteca.s7Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.primeraHipoteca.s7P1a')}
            <strong>1.5% {t('blog.primeraHipoteca.s7And')} 4%</strong>
            {t('blog.primeraHipoteca.s7P1b')}
            <RouterLink to={`/${lng}/blog/costo-de-vida-montreal-2026`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t('blog.primeraHipoteca.s7CostLink')}</RouterLink>
            {t('blog.primeraHipoteca.s7P1c')}
          </Typography>

          <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1.5, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li><strong>{t('blog.primeraHipoteca.s7Li1Label')}</strong> {t('blog.primeraHipoteca.s7Li1')}</li>
            <li><strong>{t('blog.primeraHipoteca.s7Li2Label')}</strong> {t('blog.primeraHipoteca.s7Li2')}</li>
            <li><strong>{t('blog.primeraHipoteca.s7Li3Label')}</strong> {t('blog.primeraHipoteca.s7Li3')}</li>
            <li><strong>{t('blog.primeraHipoteca.s7Li4Label')}</strong> {t('blog.primeraHipoteca.s7Li4')}</li>
            <li><strong>{t('blog.primeraHipoteca.s7Li5Label')}</strong> {t('blog.primeraHipoteca.s7Li5')}</li>
            <li><strong>{t('blog.primeraHipoteca.s7Li6Label')}</strong> {t('blog.primeraHipoteca.s7Li6')}</li>
          </Box>

          {/* Closing costs info box */}
          <Box
            sx={{
              bgcolor: '#F0FAF1',
              border: '1px solid #B8E0BA',
              borderRadius: '10px',
              p: 3,
              mb: 4,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2E7D32', mb: 1 }}>
              {t('blog.primeraHipoteca.s7ExampleTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              {t('blog.primeraHipoteca.s7ExLine1')} <strong>$500,000</strong> {t('blog.primeraHipoteca.s7ExLine1b')}:<br />
              Land Transfer Tax: ~$6,475 ({t('blog.primeraHipoteca.s7ExRebate')}: ~$2,475)<br />
              {t('blog.primeraHipoteca.s7ExLawyer')}: ~$2,000<br />
              {t('blog.primeraHipoteca.s7ExInspection')}: ~$400<br />
              {t('blog.primeraHipoteca.s7ExTitleIns')}: ~$300<br />
              {t('blog.primeraHipoteca.s7ExMoving')}: ~$2,000<br />
              <strong>{t('blog.primeraHipoteca.s7ExTotal')}: $7,175–$11,175</strong><br /><br />
              {t('blog.primeraHipoteca.s7ExRule')}
            </Typography>
          </Box>

          <Divider sx={{ mt: 5, mb: 0 }} />

          {/* FAQ Section */}
          <FAQSection faqs={FAQS} />


          {/* CTA Box: Mortgage Calculator */}
          <Box
            sx={{
              bgcolor: '#1E3A5F',
              borderRadius: '10px',
              p: 3,
              mt: 4,
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
                {t('blog.primeraHipoteca.calcCtaTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                {t('blog.primeraHipoteca.calcCtaText')}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to={`/${lng}/calculadora-hipoteca`}
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: '#fff',
                color: '#1E3A5F',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '14px',
                px: 3,
                flexShrink: 0,
                '&:hover': { bgcolor: '#E8EAF6' },
              }}
            >
              {t('blog.primeraHipoteca.calcCtaButton')}
            </Button>
          </Box>

          {/* Official Sources */}
          <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #E6E9EE' }}>
            <Typography variant="subtitle2" sx={{ color: '#5F6B7A', fontWeight: 600, mb: 1.5 }}>
              {t('blog.primeraHipoteca.sourcesTitle')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Link href="https://www.cmhc-schl.gc.ca/consumers/home-buying/mortgage-loan-insurance-for-consumers" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('blog.primeraHipoteca.source1')} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link href="https://www.canada.ca/en/financial-consumer-agency/services/mortgages.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('blog.primeraHipoteca.source2')} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link href="https://www.osfi-bsif.gc.ca/en/guidance/guidance-library/residential-mortgage-underwriting-practices-procedures" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('blog.primeraHipoteca.source3')} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link href="https://www.canada.ca/en/department-finance/news/2024/09/making-housing-more-affordable.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('blog.primeraHipoteca.source4')} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
            </Box>
            <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 2 }}>
              {t('blog.primeraHipoteca.updated')}
            </Typography>
          </Box>

          {/* Author Bio */}
          <Divider sx={{ mt: 5, mb: 0 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              p: 3,
              bgcolor: '#F7F9FC',
              borderRadius: '12px',
              mt: 3,
            }}
          >
            {AUTHOR.photo ? (
              <Box
                component="img"
                src={AUTHOR.photo}
                alt={AUTHOR.name}
                loading="lazy"
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: '#1E3A5F',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: '1.2rem',
                  flexShrink: 0,
                }}
              >
                {AUTHOR.name.split(' ').map((n) => n[0]).join('')}
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
                {AUTHOR.name}
              </Typography>
              {AUTHOR.role && (
                <Typography variant="body2" sx={{ color: '#5F6B7A' }}>
                  {AUTHOR.role}
                </Typography>
              )}
            </Box>
            {AUTHOR.linkedin && (
              <Button
                href={AUTHOR.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<LinkedIn />}
                size="small"
                sx={{
                  textTransform: 'none',
                  color: '#1E3A5F',
                  fontWeight: 600,
                  borderColor: '#1E3A5F',
                  '&:hover': { bgcolor: 'rgba(10,102,194,0.08)' },
                }}
                variant="outlined"
              >
                LinkedIn
              </Button>
            )}
          </Box>
        </Box>

        <RelatedArticles slug="primera-hipoteca-canada-guia" />
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default PrimeraHipotecaPage;
